import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Comment } from '../../types';
import { commentsService } from '../../services/api';
import { formatBlogDate, nameInitials, toFaDigits } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';
import { useBlogSettings, useIsDesktop } from './blogSettings';

interface ArticleCommentsProps {
  articleId: string;
  allowComments: boolean;
  /** شمارندهٔ اولیه از پاسخ مقاله (تا پیش از بارگذاری لیست) */
  initialCount?: number;
}

const NAME_KEY = 'blog-comment-name';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

/** یک پاسخ (رسمی یا عادی) زیر نظر */
const ReplyCard: React.FC<{ reply: Comment }> = ({ reply }) => {
  const { officialBadge, officialBadgeMobile } = useBlogSettings();
  const official = !!reply.isOfficial;
  return (
    <div
      className={`rounded-[8px] border-r-2 bg-[#f4f3f1] pb-3 pl-3 pr-3 pt-4 lg:pt-[13px] ${official ? 'border-[#4a2e68] lg:border-[#331751]' : 'border-[#cdc3d0]'}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-[6px] lg:gap-2">
          {official && (
            <span className="rounded-[4px] bg-[#331751] px-[6px] text-[10px] font-medium leading-[15px] text-white lg:hidden">{officialBadgeMobile}</span>
          )}
          {/* طبق طرح: موبایل «پاسخ رسمی + نام تیم»، دسکتاپ «پاسخ {نویسنده} + نشان تیم» */}
          {official ? (
            <span className="text-[12px] font-bold leading-5 text-[#331751] lg:text-[18px] lg:font-semibold lg:leading-[30px] lg:text-[#1a1c1a]">
              <span className="lg:hidden">{officialBadge}</span>
              <span className="hidden lg:inline">پاسخ {reply.authorName}</span>
            </span>
          ) : (
            <span className="text-[12px] font-bold leading-5 text-[#1a1c1a] lg:text-[18px] lg:font-semibold lg:leading-[30px]">{reply.authorName}</span>
          )}
          {official && (
            <span className="hidden rounded-[4px] bg-[#efdbff] px-2 py-[2px] text-[10px] font-bold leading-[15px] text-[#331751] lg:inline-block">{officialBadge}</span>
          )}
        </div>
        <time dateTime={reply.createdAt} className="text-[12px] font-medium leading-5 text-[#5b5f66]">
          {formatBlogDate(reply.createdAt)}
        </time>
      </div>
      <p className="whitespace-pre-line pt-[6px] text-[14px] leading-[26px] text-[#5b5f66] lg:pt-1 lg:leading-[22.75px]">{reply.editedContent || reply.content}</p>
    </div>
  );
};

/** بخش دیدگاه‌ها: لیست، پاسخ، فرم با همهٔ حالت‌ها */
const ArticleComments: React.FC<ArticleCommentsProps> = ({ articleId, allowComments, initialCount = 0 }) => {
  const settings = useBlogSettings();
  const isDesktop = useIsDesktop();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const [errors, setErrors] = useState<{ name?: string; content?: string }>({});
  const [state, setState] = useState<SubmitState>('idle');
  const formRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    try {
      const list = await commentsService.getArticleComments(articleId);
      setComments(Array.isArray(list) ? list : []);
    } catch {
      setComments([]);
    } finally {
      setLoaded(true);
    }
  }, [articleId]);

  useEffect(() => {
    setLoaded(false);
    setReplyTo(null);
    setState('idle');
    load();
    try {
      setName(window.localStorage.getItem(NAME_KEY) || '');
    } catch {
      /* ignore */
    }
  }, [load]);

  const count = loaded ? comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0) : initialCount;

  const focusForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => contentRef.current?.focus(), 350);
  };

  const startReply = (comment: Comment) => {
    setReplyTo(comment);
    setState('idle');
    focusForm();
  };

  const validate = () => {
    const next: { name?: string; content?: string } = {};
    if (name.trim().length < 2) next.name = 'لطفاً نام نمایشی خود را وارد کنید.';
    if (content.trim().length < 2) next.content = 'متن دیدگاه نمی‌تواند خالی باشد.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validate()) return;
    setState('submitting');
    try {
      await commentsService.createArticleComment(articleId, {
        authorName: name.trim(),
        content: content.trim(),
        parentId: replyTo?.id,
      });
      try {
        window.localStorage.setItem(NAME_KEY, name.trim());
      } catch {
        /* ignore */
      }
      setContent('');
      setReplyTo(null);
      setState('success');
    } catch {
      // متن کاربر حفظ می‌شود تا دوباره تلاش کند
      setState('error');
    }
  };

  const inputBase = 'w-full rounded-[8px] border bg-[#fdfbf7] px-[10px] text-[16px] text-[#1a1c1a] placeholder:text-[14px] placeholder:text-[rgba(91,95,102,0.6)] lg:bg-white lg:px-3 lg:text-[14px] lg:placeholder:text-[#6b7280] focus:border-[#4a2e68] focus:outline-none';

  return (
    <section id="comments" aria-labelledby="comments-heading" className="flex flex-col gap-4 border-t border-[#e9e8e5] pt-4 lg:gap-6 lg:pt-6">
      {/* هدر */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[6px] lg:gap-2">
          <span className="hidden h-[10px] w-[10px] rounded-full bg-[#331751] lg:block" aria-hidden />
          <h2 id="comments-heading" className="inline-flex items-center gap-[6px] text-[18px] font-semibold leading-[30px] text-[#331751] lg:text-[22px] lg:leading-9 lg:text-[#1a1c1a]">
            {settings.commentsTitle}
            <span className="lg:hidden">({toFaDigits(count)})</span>
            <BlogIcon name="comments-mobile" width={16.667} height={16.667} className="lg:hidden" />
          </h2>
          <span className="hidden rounded-full bg-[#e9e8e5] px-2 py-[2px] text-[12px] font-medium leading-5 text-[#5b5f66] lg:inline-block">{toFaDigits(count)}</span>
        </div>
        <span className="text-[12px] font-medium leading-5 text-[#5b5f66]">
          <span className="lg:hidden">{settings.commentsSubtitleMobile}</span>
          <span className="hidden lg:inline">{settings.commentsSubtitle}</span>
        </span>
      </div>

      {/* لیست */}
      {loaded && comments.length === 0 ? (
        <div className="flex flex-col items-start gap-3 rounded-[12px] border border-[#e6e4df] bg-white px-5 py-[23px] drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h3 className="text-[14px] font-bold leading-5 text-[#222]">هنوز دیدگاهی ثبت نشده است</h3>
            <p className="text-[12px] leading-4 text-[#666]">اولین دیدگاه را شما بنویسید و تجربه یا سوال خود را به اشتراک بگذارید.</p>
          </div>
          {allowComments && (
            <button type="button" onClick={focusForm} className="min-h-[44px] rounded-[8px] border border-[#e6e4df] bg-[#f4f3f1] px-4 text-[12px] font-semibold text-[#4a2e68] hover:bg-[#efeeeb]">
              نوشتن اولین دیدگاه
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {comments.map((comment) => (
            <article key={comment.id} className="flex flex-col gap-2 rounded-[12px] border border-[#e9e8e5] bg-[#fdfbf7] p-[14px] lg:bg-white lg:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e9e8e5] text-[12px] font-semibold leading-5 text-[#1a1c1a] lg:h-8 lg:w-8 lg:font-bold lg:text-[#5b5f66]" aria-hidden>
                    {nameInitials(comment.authorName).slice(0, 1)}
                  </span>
                  <span className="text-[14px] font-semibold leading-6 text-[#1a1c1a] lg:text-[18px] lg:leading-[30px]">{comment.authorName}</span>
                </div>
                <time dateTime={comment.createdAt} className="text-[12px] font-medium leading-5 text-[#5b5f66]">
                  {formatBlogDate(comment.createdAt)}
                </time>
              </div>
              <p className="whitespace-pre-line text-[14px] leading-[26px] text-[#1a1c1a] lg:leading-[22.75px] lg:text-[#4a454e]">{comment.editedContent || comment.content}</p>
              {allowComments && (
                <div className="flex justify-end">
                  <button type="button" onClick={() => startReply(comment)} className="inline-flex items-center gap-1 text-[12px] font-medium leading-5 text-[#4a2e68] hover:text-[#392352] lg:text-[#331751]">
                    <BlogIcon name="reply" width={12} height={9.333} />
                    پاسخ
                  </button>
                </div>
              )}
              {comment.replies?.map((reply) => (
                <ReplyCard key={reply.id} reply={reply} />
              ))}
            </article>
          ))}
        </div>
      )}

      {/* فرم */}
      {allowComments ? (
        <div ref={formRef} className="flex flex-col gap-3 rounded-[12px] border border-[#e9e8e5] bg-[#f4f3f1] px-4 pb-4 pt-6 lg:gap-4 lg:bg-[#fdfbf7] lg:p-6">
          <h3 className="inline-flex items-center justify-center gap-2 text-[18px] font-semibold leading-[30px] text-[#331751] lg:justify-start lg:text-[#1a1c1a]">
            <BlogIcon name="comments" width={16.667} height={16.667} className="hidden lg:inline-block" />
            {settings.commentFormTitle}
          </h3>

          {replyTo && (
            <div className="flex flex-col gap-1 rounded-[8px] border border-[#f4effa] bg-[rgba(244,239,250,0.4)] px-3 pb-3 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-bold leading-4 text-[#4a2e68]">پاسخ به: {replyTo.authorName}</span>
                <button type="button" onClick={() => setReplyTo(null)} className="text-[11px] font-medium leading-4 text-[#b91c1c]">
                  لغو پاسخ
                </button>
              </div>
              <p className="blog-clamp-2 text-[11px] leading-4 text-[#666]">«{(replyTo.editedContent || replyTo.content).slice(0, 120)}»</p>
            </div>
          )}

          {state === 'success' && (
            <div role="status" className="flex flex-col gap-1 rounded-[8px] border border-[#bbf7d0] bg-[#f0fdf4] p-3">
              <span className="inline-flex items-center gap-[6px] text-[12px] font-bold leading-4 text-[#15803d]">
                <BlogIcon name="check-circle" width={16} height={16} />
                دیدگاه شما با موفقیت ثبت شد
              </span>
              <span className="pb-1 text-[11px] leading-4 text-[#222]">دیدگاه شما پس از بررسی توسط تحریریه منتشر خواهد شد.</span>
              <span className="border-t border-[rgba(187,247,208,0.6)] pt-[6px] text-[10px] leading-4 text-[#666]">* این نظر تا زمان بررسی به عنوان نظر عمومی نشان داده نمی‌شود.</span>
            </div>
          )}

          {state === 'error' && (
            <div role="alert" className="flex flex-col items-end gap-1 rounded-[8px] border border-[#fecaca] bg-[rgba(254,242,242,0.6)] p-3">
              <span className="inline-flex w-full items-center gap-[6px] text-[12px] font-bold leading-4 text-[#b91c1c]">
                <BlogIcon name="error-circle" width={16} height={16} />
                خطا در ارسال دیدگاه
              </span>
              <span className="w-full text-[11px] leading-4 text-[#222]">متن شما محفوظ است. لطفاً مجدداً امتحان کنید.</span>
              <button type="button" onClick={() => submit()} className="pt-1 text-[12px] font-bold leading-4 text-[#4a2e68]">
                تلاش مجدد برای ارسال
              </button>
            </div>
          )}

          <form onSubmit={submit} noValidate className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="comment-name" className="text-[12px] font-medium leading-5 text-[#5b5f66]">
                نام نمایشی {errors.name && <span className="text-[#b91c1c]">*</span>}
              </label>
              <input
                id="comment-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={isDesktop ? settings.commentNamePlaceholder : settings.commentNamePlaceholderMobile}
                maxLength={80}
                aria-invalid={!!errors.name}
                className={`${inputBase} h-12 lg:h-11 ${errors.name ? 'border-[#b91c1c]' : 'border-[#e9e8e5]'}`}
              />
              {errors.name && (
                <span className="inline-flex items-center gap-1 text-[11px] leading-4 text-[#b91c1c]">
                  <BlogIcon name="field-error" width={14} height={14} />
                  {errors.name}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1 lg:pb-[9px]">
              <label htmlFor="comment-content" className="text-[12px] font-medium leading-5 text-[#5b5f66]">
                متن دیدگاه {errors.content && <span className="text-[#b91c1c]">*</span>}
              </label>
              <textarea
                id="comment-content"
                ref={contentRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={isDesktop ? settings.commentTextPlaceholder : settings.commentTextPlaceholderMobile}
                rows={4}
                maxLength={5000}
                aria-invalid={!!errors.content}
                className={`${inputBase} min-h-[102px] resize-y pb-2 pt-[10px] leading-[26px] lg:min-h-[124px] lg:pt-2 ${errors.content ? 'border-[#b91c1c]' : 'border-[#e9e8e5]'}`}
              />
              {errors.content && (
                <span className="inline-flex items-center gap-1 text-[11px] leading-4 text-[#b91c1c]">
                  <BlogIcon name="field-error" width={14} height={14} />
                  {errors.content}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:pt-1">
              <span className="order-2 text-center text-[12px] font-medium leading-5 text-[#5b5f66] lg:order-2 lg:text-left">{settings.commentNote}</span>
              <button
                type="submit"
                disabled={state === 'submitting'}
                className={`order-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-[8px] text-[14px] font-medium leading-6 text-white transition-colors lg:order-1 lg:h-auto lg:w-auto lg:px-4 lg:py-2 ${
                  state === 'submitting' ? 'cursor-wait bg-[rgba(74,46,104,0.7)]' : 'bg-[#331751] hover:bg-[#392352]'
                }`}
              >
                {state === 'submitting' ? (
                  <>
                    در حال ارسال…
                    <BlogIcon name="spinner" width={16} height={16} className="animate-spin" />
                  </>
                ) : (
                  <>
                    {/* طبق طرح: آیکون در موبایل سمت راست متن و در دسکتاپ سمت چپ */}
                    ارسال دیدگاه
                    <BlogIcon name="send" width={14.25} height={12} className="order-first lg:order-none" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <p className="rounded-[8px] border border-[#e9e8e5] bg-[#f4f3f1] p-3 text-center text-[12px] leading-5 text-[#5b5f66]">ثبت دیدگاه برای این مقاله غیرفعال است.</p>
      )}
    </section>
  );
};

export default ArticleComments;
